// websocket/trigger-consent.ts — the PAGE half of OWNER RULING 2026-10-02 (memory `feedback_trigger_prompt_5s`; Janus lane A10, board R-026):
// an OPTIONAL trigger (a reaction) on a PLAYER-controlled creature is a 5-second prompt to that creature's controller; no answer within 5 s
// = DECLINE; an NPC / GM-controlled creature auto-resolves. The engine half (janus-engine/ability/trigger-consent.ts) records every optional
// trigger the acting use's dry runs meet; the acting page then asks here ONCE (all questions in parallel, one shared 5 s window), fixes
// the answers, and resolves the use for real. Questions and answers travel over the encounter socket; held rows select the controller.
// WORKER PICK (owner to review): the acting page WAITS at most 5 s before its real draw (pre-ask, bounded wait, inline — a yes fires the
// reaction at the very point PHP fires it today); a relay failure for a player-controlled reactor counts as no answer (decline). The
// controller's card shows its 5 s from receipt (minus a transit margin); the acting page accepts an answer already in transit up to
// ANSWER_GRACE_MS past its own 5 s, so its wait is bounded at 6.5 s.
import { passesDbGate } from '../janus-engine/reactions/trigger-dispatch.js';
import { type TriggerConsent, type TriggerConsentRequest, consentKey } from '../janus-engine/ability/trigger-consent.js';
import { actorRowFor } from './movement-truth.js';
import { nextSpellOpportunityChoice } from '../janus-engine/sanguine/opportunity-choice.js';
import { abilityVisibility } from '../janus-engine/ability/visibility.js';
import { refuseUnported } from '../janus-engine/unported.js';
import { pushEphemeralTruth } from '../shared/ephemeral-truth';

type Row = Record<string, unknown>;
export const TRIGGER_PROMPT_WINDOW_MS = 5000;
/** How long past its deadline the acting page still accepts an answer already sent (relay transit). */
const ANSWER_GRACE_MS = 1500;
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const emit = (detail: Row): void => { window.dispatchEvent(new CustomEvent('ft:trigger-consent', { detail })); };
const viewer = (): string => upper(window.FateTide?.user?.id);
const promptClient = typeof window === 'undefined' ? '' : ((window as Window & { __ftPromptClient?: string }).__ftPromptClient ??= crypto.randomUUID().toUpperCase());
type AnswerWait = { controller: string; resolve: (answer: boolean) => void; timer: ReturnType<typeof setTimeout> };
const answersSeen = typeof window === 'undefined' ? new Set<string>()
  : ((window as Window & { __ftPromptAnswersSeen?: Set<string> }).__ftPromptAnswersSeen ??= new Set<string>());

function pushQuestion(encounterId: string, actorId: string, reactorId: string, abilityId: string, trigger: string,
  controller: string, askId: string, kind: 'question' | 'offer', abilityName = ''): boolean {
  if (!UUID.test(controller)) return false;
  return pushEphemeralTruth({ type: 'janus_trigger_prompt', phase: 'ask', operation_id: askId, ask_id: askId,
    encounter_id: upper(encounterId), actor_id: upper(actorId), reactor_id: upper(reactorId), ability_id: upper(abilityId),
    controller_user_id: controller, reactor_name: nameOf(encounterId, reactorId),
    actor_name: nameOf(encounterId, actorId), ability_name: abilityName, trigger, kind, window_ms: TRIGGER_PROMPT_WINDOW_MS });
}

/** The encounter's campaign GM as this page holds the campaign ('' = not held). */
function campaignGm(encounterId: string): string {
  const enc = asRow(window.FateTide?.encounterCache?.get?.(encounterId) ?? window.FateTide?.encounterCache?.get?.(upper(encounterId)));
  const campaignId = upper(asRow(enc?.encounters)?.encounter_master_parent_id ?? enc?.encounter_master_parent_id);
  const ft = window.FateTide as unknown as { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } | undefined;
  for (const value of [asRow(ft?.campaignCache?.get(campaignId)), asRow(ft?.currentCampaignArr)]) {
    const id = upper(asRow(value?.campaigns)?.campaign_id ?? value?.campaign_id);
    if (value && campaignId && id === campaignId) return upper(asRow(value.campaigns)?.campaign_gm ?? value.campaign_gm);
  }
  return '';
}

/** An NPC needs no prompt: the reactor's controlling creature (its
 *  controller row, else itself) must be a player character whose owner is not the GM. */
export function triggerController(encounterId: string, reactor: Row): string | null {
  const ec = asRow(reactor.encounter_creatures) ?? {};
  const controllerId = upper(ec.controller_encounter_creature_id);
  const held = UUID.test(controllerId) ? actorRowFor(encounterId, controllerId) as Row | null : null;
  // Missing controller data cannot be treated as automatic consent.
  if (UUID.test(controllerId) && !held) return '?';
  const controlling = held ?? reactor;
  const cec = asRow(controlling.encounter_creatures) ?? {};
  const type = String(scalar(asRow(controlling.items)?.item_type) || scalar(cec.item_type) || '');
  if (type !== 'player_character') return null;
  const owner = upper(cec.encounter_creature_created_by_id);
  if (!UUID.test(owner)) return null;
  const gm = campaignGm(encounterId);
  if (!gm) {
    const encounter = asRow(window.FateTide?.encounterCache?.get(upper(encounterId)));
    // Optional reactions in adventure/location encounters remain automatic (owner 2026-10-03).
    const parentType = encounter ? String(ftEncounterField(encounter, 'encounter_master_parent_type') ?? '') : '';
    if (parentType && parentType !== 'campaign') return null;
    return '?';
  }
  return owner === gm ? null : owner;
}

/** One acting use's consent reader: the answers it collected, final once asked. */
export class PageTriggerConsent implements TriggerConsent {
  final = false;
  /** how many ask rounds this use has run (askTriggerConsent) */
  rounds = 0;
  private readonly answers = new Map<string, boolean>();
  private readonly needed = new Map<string, TriggerConsentRequest>();
  constructor(private readonly encounterId: string) {}
  controller(reactor: Row): string | null { return triggerController(this.encounterId, reactor); }
  answer(reactorId: string, abilityId: string): boolean | undefined { return this.answers.get(consentKey(reactorId, abilityId)); }
  need(request: TriggerConsentRequest): void { if (!this.answers.has(request.key) && !this.needed.has(request.key)) this.needed.set(request.key, request); }
  pending(): TriggerConsentRequest[] { return [...this.needed.values()].filter(r => !this.answers.has(r.key)); }
  /** the page's own writer (askTriggerConsent) */
  settle(key: string, value: boolean): void { this.answers.set(key, value); this.needed.delete(key); }
}
export const createTriggerConsent = (encounterId: string): PageTriggerConsent => new PageTriggerConsent(encounterId);

// ONE registry per page, on window: this module is bundled into more than one bundle (websocket, pre-template, encounter-overlays via
// ability-truth.ts) and only the first copy mounts the frame listener below — every copy's questions must be settled by it.
const waiting: Map<string, AnswerWait> = typeof window !== 'undefined'
  ? ((window as unknown as { __ftTriggerConsentWaiting?: Map<string, AnswerWait> }).__ftTriggerConsentWaiting ??= new Map())
  : new Map();

/** Ask rounds per use (coordinator Codex Round 20 P2): a DECLINE can expose another optional trigger the first dry runs could not meet (a
 *  declined Evade leaves the reaction free for a later damage-triggered one) — the resolver re-runs its dry runs with the answers fixed, and a
 *  NEW candidate is asked in a further round instead of declining unasked. Bounded: after MAX_ASK_ROUNDS the reader is final (an unasked
 *  candidate then declines, as before). */
export const MAX_ASK_ROUNDS = 3;
/** Ask every pending question at once (one shared 5 s window), fix the answers; the reader turns final after MAX_ASK_ROUNDS rounds. Never throws. */
export async function askTriggerConsent(encounterId: string, actorId: string, consent: PageTriggerConsent): Promise<void> {
  const deadline = Date.now() + TRIGGER_PROMPT_WINDOW_MS;
  const requests = consent.pending().slice(0, 16);
  await Promise.all(requests.map(async request => {
    let answer = false;
    try { answer = await askOne(encounterId, actorId, request, deadline); } catch { answer = false; }
    consent.settle(request.key, answer);
    emit({ phase: 'settled', key: request.key, reactorId: request.reactorId, abilityId: request.abilityId, answer });
  }));
  for (const rest of consent.pending()) consent.settle(rest.key, false); // beyond the cap: no answer = decline
  consent.rounds += 1;
  consent.final = consent.rounds >= MAX_ASK_ROUNDS;
}

/** CROSS-LANE HELPER (lane A11 → MV3 / MV4 and every synchronous planner — planFall, planMoveTriggers, a walk's opportunity reaction): run a
 *  synchronous `plan` with a page consent reader; while its result says the controllers must be asked first (`asks`, decided before any real
 *  die), ask them (one 5 s window per round, ≤ MAX_ASK_ROUNDS rounds) and plan again with the answers fixed. */
export async function planWithConsent<T>(encounterId: string, actorId: string, plan: (consent: PageTriggerConsent) => T | Promise<T>, asks: (result: T) => boolean): Promise<T> {
  const consent = createTriggerConsent(encounterId);
  for (;;) {
    const result = await plan(consent);
    if (consent.final || !asks(result)) return result;
    await askTriggerConsent(encounterId, actorId, consent);
  }
}

async function askOne(encounterId: string, actorId: string, request: TriggerConsentRequest, deadline: number): Promise<boolean> {
  const askId = crypto.randomUUID().toUpperCase();
  if (request.controllerUserId === viewer()) {
    emit({ phase: 'asked', key: request.key, route: 'local', askId });
    return showPrompt({ askId, reactorId: request.reactorId, abilityId: request.abilityId, reactorName: nameOf(encounterId, request.reactorId),
      actorName: nameOf(encounterId, actorId), abilityName: request.abilityName, trigger: request.trigger, until: deadline, relay: false });
  }
  const answered = new Promise<boolean>(resolve => {
    // the controller's card closes at its own 5 s (started on receipt, minus a transit margin); the acting page keeps listening a short grace
    // past its deadline for an answer already in transit (Codex A10 r4 P2) — never longer than ANSWER_GRACE_MS
    const timer = setTimeout(() => { if (waiting.delete(askId)) resolve(false); }, Math.max(0, deadline - Date.now()) + ANSWER_GRACE_MS);
    waiting.set(askId, { controller: request.controllerUserId, resolve, timer });
  });
  const route = pushQuestion(encounterId, actorId, request.reactorId, request.abilityId, request.trigger,
    request.controllerUserId, askId, 'question', request.abilityName) ? 'asked' : '';
  emit({ phase: 'asked', key: request.key, route: route || 'failed', askId });
  if (route !== 'asked') { clearTimeout(waiting.get(askId)?.timer); waiting.delete(askId); return false; }
  return answered;
}

const FORTUNATE = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';
const OPPORTUNITY = 'D1EADC34-832C-1909-2FC3-722AC857FBEA';
/** The universal Fortunate's own preconditions on the row as the page holds it now: luck to spend, a reaction to spend, none held. */
function fortunateUsable(row: Row | null): boolean {
  if (!row || !passesDbGate({ value: ftLayerValue }, FORTUNATE, row, null)) return false;
  const ec = asRow(row?.encounter_creatures) ?? {};
  if (!(Number(scalar(ec.luck) ?? 0) > 0) || !(Number(scalar(ec.reaction) ?? 0) > 0)) return false;
  const held = asRow(row?.statuses)?.target;
  return !(Array.isArray(held) && held.some(s => upper(asRow(s)?.status_item_id) === FORTUNATE));
}

/** OWNER RULING 2026-10-03 — FORTUNATE ON A LUCK GAIN (memory `feedback_trigger_prompt_5s`; engine half: janus-engine/ability/trigger-consent.ts
 *  deferFortunateOffer): "after a caster gains Fortunate they get prompted if they want to use it if they have their reaction". The acting page
 *  calls this once its use / End Turn COMMITTED, for each player-controlled creature whose luck gain the verdict did not spend: if it still
 *  holds luck and a reaction and no Fortunate, its controller gets the 5 s card (local when that is this page's user, else over the relay as
 *  an `offer`); Use spends it on the CONTROLLER's page as a hand-used Fortunate (the Fortunate button's own path); silence = decline. */
export function offerFortunate(encounterId: string, actorId: string, reactorIds: readonly string[]): void {
  for (const id of new Set(reactorIds.map(upper))) void offerOne(upper(encounterId), upper(actorId), id).catch(() => undefined);
}
/** Encouraging Spell gives the recipient a choice, then opens the ordinary action-first attack picker on its controller's page. */
export function offerNextSpellOpportunity(encounterId: string, actorId: string, reactorIds: readonly string[]): void {
  for (const reactorId of new Set(reactorIds.map(upper))) void (async () => {
    const row = actorRowFor(encounterId, reactorId) as Row | null;
    if (!opportunityUsable(row)) return;
    const controller = row ? triggerController(encounterId, row) ?? campaignGm(encounterId) : null;
    if (row && !triggerController(encounterId, row) && (!controller || controller === viewer())) {
      await useNpcOpportunity(encounterId, reactorId);
      return;
    }
    const askId = crypto.randomUUID().toUpperCase();
    if (controller === viewer()) {
      if (await showPrompt({ askId, reactorId, abilityId: OPPORTUNITY, abilityName: 'Opportunity Attack',
        reactorName: nameOf(encounterId, reactorId), actorName: nameOf(encounterId, actorId),
        trigger: 'triggers_next_spell_opportunity', until: Date.now() + TRIGGER_PROMPT_WINDOW_MS, relay: false, offer: true }))
        await useOpportunityAs(encounterId, reactorId);
      return;
    }
    pushQuestion(encounterId, actorId, reactorId, OPPORTUNITY, 'triggers_next_spell_opportunity',
      controller ?? '', askId, 'offer', 'Opportunity Attack');
  })().catch(() => undefined);
}
function opportunityUsable(row: Row | null): boolean {
  return !!row && Number(ftEncounterCreatureField(row.encounter_creatures, 'reaction') ?? 0) > 0
    && Number(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_dead') ?? 0) === 0;
}
async function useNpcOpportunity(encounterId: string, reactorId: string): Promise<void> {
  const w = window as unknown as { ftTryCommitWeaponAttack?: (input: { encounterId: string; actorId: string; targetId: string;
    weaponId: string; opportunityAttack: true }) => Promise<unknown>;
    fetchAndCacheObject?: (id: string) => Promise<unknown>; ftFindFullNestedItem?: (id: string) => unknown };
  const caster = actorRowFor(encounterId, reactorId) as Row | null;
  const encounter = asRow(window.FateTide?.encounterCache?.get(encounterId));
  if (!caster || !encounter || !opportunityUsable(caster)) return;
  if (!w.ftFindFullNestedItem?.(OPPORTUNITY)) await w.fetchAndCacheObject?.(OPPORTUNITY);
  const choice = nextSpellOpportunityChoice(caster, (encounter.encounter_creatures_arr ?? []) as Row[], {
    value: (row, key) => row ? ftLayerValue(row, key) : null, has: (row, key) => !!row && ftLayerValue(row, key) != null,
    entries: (row, key) => row ? ftLayerEntries(row, key) : [], statuses: row => ftCreatureStatuses(row, 'target') as Row[],
    itemField: ftItemsCol, itemType: row => String(ftItemsCol(row, 'item_type') ?? ''), inventoryField: ftInventoryField,
    resolveItem: id => asRow(w.ftFindFullNestedItem?.(String(id ?? ''))),
    inCombat: () => true, creatureSizeId: row => ftEffectiveSizeId(row),
    visible: (from, to) => abilityVisibility(encounter, from, to) === 'visible',
  });
  if (choice && 'reason' in choice) {
    refuseUnported(`next-spell-unported:next_spell_target_opportunity_attack:${choice.reason}`);
    return;
  }
  if (choice) {
    if (!w.ftTryCommitWeaponAttack) { refuseUnported('next-spell-unported:next_spell_target_opportunity_attack:attack-lane-not-held'); return; }
    await w.ftTryCommitWeaponAttack({ encounterId, actorId: reactorId, ...choice, opportunityAttack: true });
  }
}
async function useOpportunityAs(encounterId: string, reactorId: string): Promise<void> {
  const w = window as unknown as { ftGetActiveCasterId?: () => unknown; ftSetActiveCaster?: (id: string) => unknown;
    ftArmSanguineOpportunity?: (id: string, commit: (targetId: string, weaponId: string) => void) => boolean;
    ftTryCommitWeaponAttack?: (input: { encounterId: string; actorId: string; targetId: string; weaponId: string; opportunityAttack: true }) => Promise<unknown> };
  if (!opportunityUsable(actorRowFor(encounterId, reactorId) as Row | null)) return;
  if (upper(w.ftGetActiveCasterId?.()) !== reactorId) {
    await w.ftSetActiveCaster?.(reactorId);
    if (upper(w.ftGetActiveCasterId?.()) !== reactorId) return;
  }
  emit({ phase: 'offer-used', reactorId, abilityId: OPPORTUNITY });
  if (!w.ftArmSanguineOpportunity || !w.ftTryCommitWeaponAttack) {
    refuseUnported('next-spell-unported:next_spell_target_opportunity_attack:picker-not-held'); return;
  }
  const commit = w.ftTryCommitWeaponAttack;
  if (!w.ftArmSanguineOpportunity(reactorId, (targetId, weaponId) => {
    void commit({ encounterId, actorId: reactorId, targetId, weaponId, opportunityAttack: true });
  })) refuseUnported('next-spell-unported:next_spell_target_opportunity_attack:picker-not-ready');
}
async function offerOne(encounterId: string, actorId: string, reactorId: string): Promise<void> {
  const row = actorRowFor(encounterId, reactorId) as Row | null;
  if (!row || !fortunateUsable(row)) return;
  const controllerUserId = triggerController(encounterId, row);
  if (!controllerUserId || controllerUserId === '?') return; // the engine defers only a KNOWN player's gain
  const until = Date.now() + TRIGGER_PROMPT_WINDOW_MS;
  const askId = crypto.randomUUID().toUpperCase();
  emit({ phase: 'offered', reactorId, abilityId: FORTUNATE, askId, route: controllerUserId === viewer() ? 'local' : 'relay' });
  if (controllerUserId === viewer()) {
    if (await showPrompt({ askId, reactorId, abilityId: FORTUNATE, reactorName: nameOf(encounterId, reactorId), actorName: '', abilityName: 'Fortunate',
      trigger: 'caster_gains_luck', until, relay: false, offer: true })) await useFortunateAs(encounterId, reactorId);
    return;
  }
  pushQuestion(encounterId, actorId, reactorId, FORTUNATE, 'caster_gains_luck', controllerUserId, askId, 'offer', 'Fortunate');
}
/** Spend Fortunate as `reactorId` on THIS (the controller's) page — exactly the Fortunate button (its gate, the lanes, the legacy fallback),
 *  which acts as the ACTIVE caster: pivot to the creature first when it is not, and cast nothing when the pivot is refused. */
async function useFortunateAs(encounterId: string, reactorId: string): Promise<void> {
  const w = window as unknown as { ftGetActiveCasterId?: () => unknown; ftSetActiveCaster?: (id: string) => unknown; handleAbilityBtnSingleClick?: (id: string) => unknown };
  if (!fortunateUsable(actorRowFor(encounterId, reactorId) as Row | null)) return;
  if (upper(w.ftGetActiveCasterId?.()) !== reactorId) {
    try { await w.ftSetActiveCaster?.(reactorId); } catch { /* refused below */ }
    if (upper(w.ftGetActiveCasterId?.()) !== reactorId) {
      if (typeof displayWarning === 'function') displayWarning(`Select ${nameOf(encounterId, reactorId) || 'that creature'} and use Fortunate from its card.`);
      return;
    }
  }
  emit({ phase: 'offer-used', reactorId, abilityId: FORTUNATE });
  await w.handleAbilityBtnSingleClick?.(FORTUNATE);
}

function nameOf(encounterId: string, id: string): string {
  if (upper(id) === upper(encounterId)) return 'Game Master';
  const row = actorRowFor(encounterId, id) as Row | null;
  return String(scalar(asRow(row?.encounter_creatures)?.encounter_creature_name) ?? scalar(asRow(row?.items)?.item_name) ?? '');
}

/** The fixed column the cards stack in (bottom-centre, above the dock). */
function promptStack(): HTMLElement {
  let stack = document.getElementById('ft-trigger-prompts');
  if (!stack) {
    stack = document.createElement('div');
    stack.id = 'ft-trigger-prompts';
    stack.style.cssText = 'position:fixed;left:50%;bottom:96px;transform:translateX(-50%);z-index:2147483000;display:flex;flex-direction:column-reverse;gap:8px;'
      + 'align-items:center;pointer-events:none;max-height:calc(100vh - 140px);overflow:auto';
    document.body.appendChild(stack);
  }
  return stack;
}

interface PromptSpec { askId: string; reactorId: string; abilityId: string; reactorName: string; actorName: string; abilityName: string; trigger: string; until: number; relay: boolean;
  encounterId?: string; controller?: string;
  /** a Fortunate OFFER after a luck gain (owner 2026-10-03): the card's Use spends it on this page */
  offer?: boolean }

/** The bridge claims one answer. Only the tab whose answer echoes may spend an offer. */
function sendAnswer(spec: PromptSpec, answer: boolean): Promise<boolean> {
  if (answersSeen.has(spec.askId)) return Promise.resolve(false);
  return new Promise(resolve => {
    const operationId = crypto.randomUUID().toUpperCase();
    const finish = (ok: boolean): void => {
      clearTimeout(timer); window.removeEventListener('ft:trigger-prompt-answered', received);
      window.removeEventListener('ft:ephemeral-truth-refused', refused); resolve(ok);
    };
    const received = (event: Event): void => {
      const frame = (event as CustomEvent<Row>).detail;
      if (upper(frame.ask_id) === spec.askId && upper(frame.controller_user_id) === spec.controller)
        finish(frame.answer === true && upper(frame.answer_client_id) === promptClient);
    };
    const refused = (event: Event): void => { if ((event as CustomEvent<Row>).detail.operation_id === operationId) finish(false); };
    const timer = setTimeout(() => finish(false), 4000);
    window.addEventListener('ft:trigger-prompt-answered', received);
    window.addEventListener('ft:ephemeral-truth-refused', refused);
    if (!pushEphemeralTruth({ type: 'janus_trigger_prompt', phase: 'answer', operation_id: operationId,
      encounter_id: spec.encounterId ?? '', actor_id: spec.reactorId, reactor_id: spec.reactorId, ability_id: spec.abilityId,
      ask_id: spec.askId, controller_user_id: spec.controller ?? '', answer_client_id: promptClient, answer })) finish(false);
  });
}

/** Page-side bounds: ONE card per creature — a second question about the same
 *  creature joins that card as another line — at most MAX_CARDS creatures' cards and MAX_LINES lines per card; anything beyond is dropped at
 *  once (= no answer = decline), never queued. */
const MAX_CARDS = 4, MAX_LINES = 4;
function creatureCard(spec: PromptSpec): HTMLElement | null {
  const stack = promptStack();
  const key = UUID.test(spec.reactorId) ? spec.reactorId : `ask:${spec.askId}`;
  let card = Array.from(stack.children).find((el): el is HTMLElement => el instanceof HTMLElement && el.getAttribute('data-ft-trigger-reactor') === key) ?? null;
  if (card) return card.querySelectorAll('[data-ft-trigger-prompt]').length >= MAX_LINES ? null : card;
  if (stack.children.length >= MAX_CARDS) return null;
  card = document.createElement('div');
  card.setAttribute('data-ft-trigger-reactor', key);
  card.setAttribute('role', 'alertdialog');
  card.style.cssText = 'max-width:min(420px,calc(100vw - 32px));pointer-events:auto;display:flex;flex-direction:column;gap:10px;'
    + 'background:#1d1a24;color:#f2eee6;border:1px solid #8a6cff;border-radius:10px;padding:12px 14px;box-shadow:0 6px 24px rgba(0,0,0,.5);font:14px/1.4 system-ui,sans-serif';
  stack.appendChild(card); // several creatures' cards STACK (Codex A10 r3 P1: one fixed spot let a later card cover an earlier one)
  return card;
}

/** The 5 s card on the CONTROLLER's page. Resolves true on Use, false on Decline or when the window closes (or when the page is already
 *  showing its bound of cards / lines — dropped, a decline). */
function showPrompt(spec: PromptSpec): Promise<boolean> {
  return new Promise(resolve => {
    let done = false;
    const card = creatureCard(spec);
    if (!card) { emit({ phase: 'prompt-dropped', askId: spec.askId, reactorId: spec.reactorId }); resolve(false); return; }
    const host = document.createElement('div');
    host.setAttribute('data-ft-trigger-prompt', spec.askId);
    const title = document.createElement('div');
    title.style.cssText = 'font-weight:600;margin-bottom:4px';
    title.textContent = `${spec.abilityName || 'Reaction'} — use it?`;
    const text = document.createElement('div');
    text.style.cssText = 'opacity:.85;margin-bottom:8px';
    const when = spec.trigger.replace(/^triggers_/, '').replace(/_/g, ' ');
    text.textContent = spec.offer && spec.abilityId === OPPORTUNITY
      ? `${spec.reactorName || 'Your creature'} can use its reaction for an Opportunity Attack. Use opens target selection. No answer in 5 s = decline.`
      : spec.offer
      ? `${spec.reactorName || 'Your creature'} gained luck: spend 1 luck and your reaction on Fortunate? No answer in 5 s = decline.`
      : spec.trigger === 'before_turn_start_refresh'
        ? `${spec.reactorName || 'Your creature'}'s turn is about to start and it still holds its reaction: spend it and 1 luck on Fortunate now? No answer in 5 s = decline.`
        : `${spec.reactorName || 'Your creature'} can react${spec.actorName ? ` to ${spec.actorName}` : ''}${when ? ` (${when})` : ''}. No answer in 5 s = decline.`;
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:8px;align-items:center';
    const use = document.createElement('button'); use.type = 'button'; use.textContent = 'Use'; use.setAttribute('data-ft-trigger-answer', 'yes');
    const no = document.createElement('button'); no.type = 'button'; no.textContent = 'Decline'; no.setAttribute('data-ft-trigger-answer', 'no');
    for (const b of [use, no]) b.style.cssText = 'padding:4px 14px;border-radius:6px;border:1px solid #8a6cff;background:' + (b === use ? '#8a6cff' : 'transparent') + ';color:#fff;cursor:pointer';
    const clock = document.createElement('span'); clock.style.cssText = 'margin-left:auto;font-variant-numeric:tabular-nums;opacity:.8';
    row.append(use, no, clock); host.append(title, text, row);
    card.appendChild(host);
    const tick = (): void => { clock.textContent = `${Math.max(0, Math.ceil((spec.until - Date.now()) / 1000))}s`; };
    tick();
    const interval = setInterval(tick, 250);
    const remove = (): void => {
      clearInterval(interval); clearTimeout(timer); host.remove();
      window.removeEventListener('ft:trigger-prompt-answered', onAnswer);
      if (!card.querySelector('[data-ft-trigger-prompt]')) card.remove();
    };
    const onAnswer = (event: Event): void => {
      const frame = (event as CustomEvent<Row>).detail;
      if (upper(frame.ask_id) !== spec.askId || upper(frame.controller_user_id) !== spec.controller) return;
      done = true; remove();
      resolve(false); // this tab has not answered; the winning tab's sendAnswer owns the spend
    };
    window.addEventListener('ft:trigger-prompt-answered', onAnswer);
    const finish = (answer: boolean, by: 'click' | 'timeout'): void => {
      if (done) return; done = true;
      remove();
      emit({ phase: 'prompt-closed', askId: spec.askId, answer, by });
      if (spec.relay && by === 'click') {
        void sendAnswer(spec, answer).then(resolve); return;
      }
      resolve(answer);
    };
    const timer = setTimeout(() => finish(false, 'timeout'), Math.max(0, spec.until - Date.now()));
    use.addEventListener('click', () => finish(true, 'click'));
    no.addEventListener('click', () => finish(false, 'click'));
  });
}

// The cross-bundle entry for a lane whose luck gain happens outside the ability / End Turn pages (the weapon attack's natural 20, a trap save):
// offer Fortunate after ITS commit (owner 2026-10-03). Every bundle copy assigns the same behaviour.
if (typeof window !== 'undefined') Object.assign(window, { ftOfferFortunate: offerFortunate });
if (typeof window !== 'undefined') Object.assign(window, { ftOfferNextSpellOpportunity: offerNextSpellOpportunity });

// The relay's frames: an 'ask' for a creature this user controls shows the card; an 'answer' settles this page's own question.
if (typeof window !== 'undefined' && !(window as unknown as { __ftTriggerConsentMounted?: boolean }).__ftTriggerConsentMounted) {
  (window as unknown as { __ftTriggerConsentMounted?: boolean }).__ftTriggerConsentMounted = true;
  window.addEventListener('ft:janus-trigger-prompt', (event: Event) => {
    const frame = asRow((event as CustomEvent).detail);
    const askId = upper(frame?.ask_id);
    if (!frame || !UUID.test(askId)) return;
    if (frame.phase === 'answer') {
      const pending = waiting.get(askId);
      if (upper(frame.controller_user_id) !== (pending?.controller ?? viewer())) return;
      if (answersSeen.has(askId)) return;
      answersSeen.add(askId);
      if (answersSeen.size > 512) answersSeen.delete(answersSeen.values().next().value!);
      if (pending) { clearTimeout(pending.timer); waiting.delete(askId); pending.resolve(frame.answer === true); }
      window.dispatchEvent(new CustomEvent('ft:trigger-prompt-answered', { detail: frame }));
      return;
    }
    if (frame.phase !== 'ask' || window.top !== window) return; // one card per page (the scene iframe loads the same bundle)
    if (upper(frame.controller_user_id) !== viewer() || answersSeen.has(askId)) return;
    const windowMs = Math.max(0, Math.min(TRIGGER_PROMPT_WINDOW_MS, Number(frame.window_ms ?? TRIGGER_PROMPT_WINDOW_MS)));
    // an OFFER (owner 2026-10-03: Fortunate after a luck gain) is spent HERE, by the controller, on Use — the asker waits for nothing
    const opportunity = upper(frame.ability_id) === OPPORTUNITY;
    const offer = frame.kind === 'offer' && (upper(frame.ability_id) === FORTUNATE || opportunity);
    const reactorId = upper(frame.reactor_id), encounterId = upper(frame.encounter_id);
    const until = Date.now() + Math.max(0, windowMs - 300);
    void (async () => {
      // The sender's committed frame can outrun this controller's asynchronous roster adoption. Heal a stale offer before its usable-row gate.
      const usable = opportunity ? opportunityUsable : fortunateUsable;
      if (offer && !usable(actorRowFor(encounterId, reactorId) as Row | null)) {
        try { await window.fetchAndCacheCaster?.(reactorId, { skipEncounterSeed: true, throwOnError: true, participantDeadline: true }); } catch { /* gate below */ }
        if (Date.now() >= until || !usable(actorRowFor(encounterId, reactorId) as Row | null)) { emit({ phase: 'offer-dropped', askId, reactorId }); return false; }
      }
      const reactor = actorRowFor(encounterId, reactorId) as Row | null;
      if (offer && opportunity && reactor && !triggerController(encounterId, reactor)) {
        if (await sendAnswer({ askId, reactorId, abilityId: OPPORTUNITY, reactorName: '', actorName: '', abilityName: '',
          trigger: '', until, relay: true, encounterId, controller: upper(frame.controller_user_id) }, true))
          await useNpcOpportunity(encounterId, reactorId);
        return false;
      }
      return showPrompt({ askId, reactorId, abilityId: upper(frame.ability_id), reactorName: String(frame.reactor_name ?? ''),
        actorName: String(frame.actor_name ?? ''), abilityName: String(frame.ability_name ?? ''), trigger: String(frame.trigger ?? ''),
        // the asker's own window minus the relay transit (Codex A10 r3 P2): the card never outlives the acting page's deadline
        until, relay: true, offer, encounterId, controller: upper(frame.controller_user_id) });
    })()
      .then(answer => {
        if (offer && answer && UUID.test(reactorId) && UUID.test(encounterId))
          return opportunity ? useOpportunityAs(encounterId, reactorId) : useFortunateAs(encounterId, reactorId);
        return undefined;
      })
      .catch(() => undefined);
  });
}

